import api from './api';

const resourceAllocationService = {
  // Get all resource allocations with filtering and pagination
  getAll: async (params = {}) => {
    const response = await api.get('/resource-allocations', { params });
    return response.data;
  },

  // Get allocation by ID
  getById: async (id) => {
    const response = await api.get(`/resource-allocations/${id}`);
    return response.data;
  },

  // Create new allocation
  create: async (allocationData) => {
    const response = await api.post('/resource-allocations', allocationData);
    return response.data;
  },

  // Update allocation
  update: async (id, allocationData) => {
    const response = await api.put(`/resource-allocations/${id}`, allocationData);
    return response.data;
  },

  // Delete allocation
  delete: async (id) => {
    const response = await api.delete(`/resource-allocations/${id}`);
    return response.data;
  },

  // Get allocations by project
  getByProject: async (projectId) => {
    const response = await api.get(`/resource-allocations/project/${projectId}`);
    return response.data;
  },

  // Get allocations by user
  getByUser: async (userId) => {
    const response = await api.get(`/resource-allocations/user/${userId}`);
    return response.data;
  },

  // Get utilization statistics
  getUtilization: async (params = {}) => {
    const response = await api.get('/resource-allocations/utilization', { params });
    return response.data;
  },

  // Helper: Calculate utilization percentage
  calculateUtilization: (allocations) => {
    const totalAllocation = allocations.reduce((sum, alloc) => sum + alloc.allocation_percentage, 0);
    return Math.min(totalAllocation, 100);
  },

  // Helper: Check if over-allocated
  isOverAllocated: (allocations) => {
    const totalAllocation = allocations.reduce((sum, alloc) => sum + alloc.allocation_percentage, 0);
    return totalAllocation > 100;
  },

  // Helper: Get utilization color
  getUtilizationColor: (utilizationPercentage) => {
    if (utilizationPercentage > 100) return 'bg-red-100 text-red-800 border-red-300';
    if (utilizationPercentage === 100) return 'bg-green-100 text-green-800 border-green-300';
    if (utilizationPercentage >= 80) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-blue-100 text-blue-800 border-blue-300';
  },

  // Helper: Get utilization badge color
  getUtilizationBadgeColor: (utilizationPercentage) => {
    if (utilizationPercentage > 100) return 'bg-red-500 text-white';
    if (utilizationPercentage === 100) return 'bg-green-500 text-white';
    if (utilizationPercentage >= 80) return 'bg-yellow-500 text-white';
    return 'bg-blue-500 text-white';
  },

  // Helper: Get utilization status
  getUtilizationStatus: (utilizationPercentage) => {
    if (utilizationPercentage > 100) return 'Over-allocated';
    if (utilizationPercentage === 100) return 'Fully Allocated';
    if (utilizationPercentage >= 80) return 'Well Allocated';
    return 'Under-allocated';
  },

  // Helper: Format allocation percentage
  formatAllocationPercentage: (percentage) => {
    return `${percentage}%`;
  },

  // Helper: Calculate total billable amount
  calculateBillableAmount: (allocation, hours) => {
    if (!allocation.billable_rate || !hours) return 0;
    return (parseFloat(allocation.billable_rate) * parseFloat(hours)).toFixed(2);
  },

  // Helper: Check if allocation is active
  isActive: (allocation) => {
    const now = new Date();
    const startDate = new Date(allocation.start_date);
    const endDate = allocation.end_date ? new Date(allocation.end_date) : null;

    if (now < startDate) return false;
    if (endDate && now > endDate) return false;
    return true;
  },

  // Helper: Get allocation status
  getAllocationStatus: (allocation) => {
    const now = new Date();
    const startDate = new Date(allocation.start_date);
    const endDate = allocation.end_date ? new Date(allocation.end_date) : null;

    if (now < startDate) return 'Upcoming';
    if (endDate && now > endDate) return 'Completed';
    return 'Active';
  },

  // Helper: Get allocation status color
  getAllocationStatusColor: (status) => {
    const colors = {
      Active: 'badge-success',
      Upcoming: 'badge-info',
      Completed: 'badge-secondary'
    };
    return colors[status] || 'badge-secondary';
  },

  // Helper: Calculate allocation duration in days
  getAllocationDuration: (allocation) => {
    const startDate = new Date(allocation.start_date);
    const endDate = allocation.end_date ? new Date(allocation.end_date) : new Date();
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  },

  // Helper: Format date for display
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  },

  // Helper: Validate allocation data
  validateAllocationData: (allocationData) => {
    const errors = {};

    if (!allocationData.project_id) {
      errors.project_id = 'Project is required';
    }
    if (!allocationData.user_id) {
      errors.user_id = 'User is required';
    }
    if (!allocationData.allocation_percentage) {
      errors.allocation_percentage = 'Allocation percentage is required';
    } else if (allocationData.allocation_percentage < 0 || allocationData.allocation_percentage > 100) {
      errors.allocation_percentage = 'Allocation percentage must be between 0 and 100';
    }
    if (!allocationData.start_date) {
      errors.start_date = 'Start date is required';
    }
    if (allocationData.end_date && allocationData.start_date) {
      const start = new Date(allocationData.start_date);
      const end = new Date(allocationData.end_date);
      if (end < start) {
        errors.end_date = 'End date must be after start date';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  },

  submitLeaveAvailability: async (payload) => {
    const response = await api.post('/resource-leaves', payload);
    return response.data;
  },

  // Helper: Group allocations by user
  groupByUser: (allocations) => {
    return allocations.reduce((acc, allocation) => {
      const userId = allocation.user_id;
      if (!acc[userId]) {
        acc[userId] = {
          user: allocation.user,
          allocations: [],
          totalAllocation: 0
        };
      }
      acc[userId].allocations.push(allocation);
      acc[userId].totalAllocation += allocation.allocation_percentage;
      return acc;
    }, {});
  },

  // Helper: Group allocations by project
  groupByProject: (allocations) => {
    return allocations.reduce((acc, allocation) => {
      const projectId = allocation.project_id;
      if (!acc[projectId]) {
        acc[projectId] = {
          project: allocation.project,
          allocations: [],
          totalResources: 0
        };
      }
      acc[projectId].allocations.push(allocation);
      acc[projectId].totalResources++;
      return acc;
    }, {});
  },

  // Helper: Filter active allocations
  filterActive: (allocations) => {
    return allocations.filter(allocation => resourceAllocationService.isActive(allocation));
  },

  // Helper: Filter by date range
  filterByDateRange: (allocations, startDate, endDate) => {
    return allocations.filter(allocation => {
      const allocStart = new Date(allocation.start_date);
      const allocEnd = allocation.end_date ? new Date(allocation.end_date) : new Date();
      const rangeStart = new Date(startDate);
      const rangeEnd = new Date(endDate);

      return (allocStart <= rangeEnd && allocEnd >= rangeStart);
    });
  },

  // Helper: Get allocation summary
  getAllocationSummary: (allocations) => {
    const summary = {
      total: allocations.length,
      active: 0,
      upcoming: 0,
      completed: 0,
      totalAllocationPercentage: 0,
      averageAllocationPercentage: 0,
      overAllocated: 0
    };

    let totalPercentage = 0;

    allocations.forEach(allocation => {
      const status = resourceAllocationService.getAllocationStatus(allocation);
      if (status === 'Active') summary.active++;
      else if (status === 'Upcoming') summary.upcoming++;
      else if (status === 'Completed') summary.completed++;

      totalPercentage += allocation.allocation_percentage;
    });

    summary.totalAllocationPercentage = totalPercentage;
    summary.averageAllocationPercentage = allocations.length > 0
      ? (totalPercentage / allocations.length).toFixed(2)
      : 0;

    // Count over-allocated users
    const byUser = resourceAllocationService.groupByUser(allocations);
    Object.values(byUser).forEach(userData => {
      if (userData.totalAllocation > 100) {
        summary.overAllocated++;
      }
    });

    return summary;
  },

  // Helper: Sort allocations by start date
  sortByStartDate: (allocations, ascending = true) => {
    return [...allocations].sort((a, b) => {
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return ascending ? dateA - dateB : dateB - dateA;
    });
  },

  // Helper: Sort allocations by allocation percentage
  sortByAllocationPercentage: (allocations, ascending = false) => {
    return [...allocations].sort((a, b) => {
      return ascending
        ? a.allocation_percentage - b.allocation_percentage
        : b.allocation_percentage - a.allocation_percentage;
    });
  }
};

export default resourceAllocationService;

